import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@workspace/ui/components/tabs';
import { cn } from '@workspace/ui/lib/utils';
import { useState } from 'react';
import { m } from '~/paraglide/messages.js';
import type { Transaction } from '../../-types/card';

const getTabs = () => [
  { label: m['cards.transactions.tabs.all'](), value: 'todas' },
  { label: m['cards.transactions.tabs.incoming'](), value: 'entrantes' },
  { label: m['cards.transactions.tabs.outgoing'](), value: 'salientes' },
];

function getInitials(name: string): string {
  if (!name) return '—';
  const words = name.trim().split(' ');
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return words[0].slice(0, 2).toUpperCase();
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const month = date.toLocaleDateString('es-ES', { month: 'short' });
  const day = date.getDate();
  return `${month.charAt(0).toUpperCase() + month.slice(1)} ${day}`;
}

type Props = {
  title?: string;
  transactions: Transaction[];
  isLoading: boolean;
  tab: string;
  onTabChange: (tab: string) => void;
};

export function Transactions({
  title,
  transactions,
  isLoading,
  tab,
  onTabChange,
}: Props) {
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);

  const tabs = getTabs();
  const displayTitle = title || m['cards.transactions.title']();

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium">{displayTitle}</h2>
      </div>

      <Tabs defaultValue={tab} onValueChange={onTabChange} className="mb-4">
        <TabsList className="rounded-md p-1 h-auto">
          {tabs.map((tabItem) => {
            return (
              <TabsTrigger
                key={tabItem.value}
                value={tabItem.value}
                className="px-3"
              >
                {tabItem.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          {isLoading ? (
            <div className="border border-purple-500/20 rounded-lg overflow-hidden">
              <table className="w-full">
                <tbody>
                  {[...Array(4)].map((_, idx) => (
                    <tr
                      key={idx}
                      className="border-b border-purple-500/10 animate-pulse"
                    >
                      <td className="p-4">
                        <div className="h-4 w-16 bg-muted rounded" />
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="bg-muted rounded-full w-8 h-8" />
                          <div className="h-4 w-32 bg-muted rounded" />
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="h-4 w-24 bg-muted rounded" />
                      </td>
                      <td className="p-4">
                        <div className="h-4 w-32 bg-muted rounded" />
                      </td>
                      <td className="p-4">
                        <div className="h-4 w-28 bg-muted rounded" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : transactions.length === 0 ? (
            <div className="border border-purple-500/20 rounded-lg p-8 text-center text-muted-foreground">
              {m['cards.transactions.empty']()}{' '}
              {tab !== 'todas' &&
                `${tabs.find((t) => t.value === tab)?.label.toLowerCase()} ${m['cards.transactions.empty_filtered']()}`}
            </div>
          ) : (
            <div className="rounded-lg border border-purple-500/20 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-purple-500/10">
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                      {m['cards.transactions.table.date']()}
                    </th>
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                      {m['cards.transactions.table.from_to']()}
                    </th>
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                      {m['cards.transactions.table.amount']()}
                    </th>
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                      {m['cards.transactions.table.account']()}
                    </th>
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                      {m['cards.transactions.table.method']()}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction, idx) => {
                    const meta = transaction.details?.metadata || {};
                    const isIncome = transaction.direction === 'in';
                    const sign = isIncome ? '+' : '-';
                    const merchant =
                      meta.merchant_name?.trim() ||
                      m['cards.transactions.default_merchant']();
                    const category =
                      meta.transaction_type ||
                      m['cards.transactions.default_category']();
                    const id =
                      (meta.transaction_id || transaction.reference || idx) +
                      '' +
                      idx;

                    const [currency, decimals] = transaction.asset.split('/');
                    const normalizedAmount =
                      Number(transaction.amount) * 10 ** -decimals;

                    const initials = getInitials(merchant);

                    const operationType =
                      meta.operation_type ||
                      m['cards.transactions.default_operations']();
                    const accountInfo = operationType
                      .split('_')
                      .map(
                        (word) => word.charAt(0) + word.slice(1).toLowerCase(),
                      )
                      .join('/');

                    return (
                      <tr
                        key={id}
                        className="border-b border-purple-500/10 hover:bg-purple-500/5 cursor-pointer transition-colors"
                        onClick={() => setSelectedTransaction(transaction)}
                      >
                        <td className="p-4 text-sm">
                          {formatDate(transaction.created_at)}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="bg-muted text-white rounded-full w-8 h-8 flex items-center justify-center text-xs font-medium">
                              {initials}
                            </div>
                            <span className="text-sm">{merchant}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <span
                            className={cn(
                              'text-sm font-medium',
                              isIncome ? 'text-green-500' : 'text-red-400',
                            )}
                          >
                            {sign}$
                            {normalizedAmount.toLocaleString('en-US', {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 2,
                            })}{' '}
                            {currency}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {accountInfo}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {category}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog
        open={!!selectedTransaction}
        onOpenChange={(open) => !open && setSelectedTransaction(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{m['cards.transactions.detail.title']()}</DialogTitle>
          </DialogHeader>
          {selectedTransaction &&
            (() => {
              const meta = selectedTransaction.details?.metadata || {};
              const isIncome = selectedTransaction.direction === 'in';
              const sign = isIncome ? '+' : '-';
              const merchant =
                meta.merchant_name?.trim() ||
                m['cards.transactions.default_merchant']();
              const date = new Date(
                selectedTransaction.created_at,
              ).toLocaleDateString();
              const time = new Date(
                selectedTransaction.created_at,
              ).toLocaleTimeString();
              const category =
                meta.transaction_type ||
                m['cards.transactions.default_category']();
              const transactionId =
                meta.transaction_id || selectedTransaction.reference || 'N/A';

              const [currency, decimals] = selectedTransaction.asset.split('/');
              const normalizedAmount =
                Number(selectedTransaction.amount) * 10 ** -decimals;
              const isDUSD = currency === 'DUSD';
              const [units, cents] = parseFloat(normalizedAmount.toString())
                .toFixed(2)
                .split('.');

              return (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {m['cards.transactions.detail.amount']()}
                      </p>
                      <p
                        className={cn(
                          'text-2xl font-bold',
                          isIncome ? 'text-chart-5' : 'text-chart-2',
                        )}
                      >
                        {sign}${isDUSD ? `${units},` : normalizedAmount}
                        {isDUSD && <sup>{cents}</sup>}
                        {currency && (
                          <span className="ml-1 text-sm">{currency}</span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {m['cards.transactions.detail.merchant']()}
                      </p>
                      <p className="font-medium">{merchant}</p>
                    </div>

                    <div>
                      <p className="text-sm text-muted-foreground">
                        {m['cards.transactions.detail.transaction_id']()}
                      </p>
                      <p className="font-mono text-sm break-all">
                        {transactionId}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-muted-foreground">
                        {m['cards.transactions.detail.type']()}
                      </p>
                      <p className="font-medium">
                        {isIncome
                          ? m['cards.transactions.detail.type_income']()
                          : m['cards.transactions.detail.type_expense']()}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-muted-foreground">
                        {m['cards.transactions.detail.category']()}
                      </p>
                      <p className="font-medium">{category}</p>
                    </div>

                    <div>
                      <p className="text-sm text-muted-foreground">
                        {m['cards.transactions.detail.date']()}
                      </p>
                      <p className="font-medium">
                        {date} - {time}
                      </p>
                    </div>

                    {selectedTransaction.reference && (
                      <div>
                        <p className="text-sm text-muted-foreground">
                          {m['cards.transactions.detail.reference']()}
                        </p>
                        <p className="font-mono text-sm break-all">
                          {selectedTransaction.reference}
                        </p>
                      </div>
                    )}

                    {selectedTransaction.rail_name && (
                      <div>
                        <p className="text-sm text-muted-foreground">
                          {m['cards.transactions.detail.network']()}
                        </p>
                        <p className="font-medium">
                          {selectedTransaction.rail_name}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
